import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { trackAddToCart, trackAddToWishlist } from "@/lib/pixel";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const load = (k, fb) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? fb; } catch { return fb; }
};

// Wishlist entries are { id, size, colour, saved_price, note }. Normalises legacy id-only lists.
const normWishlist = (arr) => (arr || []).map((w) =>
  (typeof w === "object" && w !== null)
    ? { id: String(w.id), size: w.size ?? null, colour: w.colour ?? null, saved_price: w.saved_price ?? null, note: w.note ?? null }
    : { id: String(w), size: null, colour: null, saved_price: null, note: null });
const wKey = (id, size, colour) => `${id}::${size || ""}::${colour || ""}`;

export function StoreProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(() => load("hb_cart", []));
  const [wishlist, setWishlist] = useState(() => normWishlist(load("hb_wishlist", [])));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => { localStorage.setItem("hb_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("hb_wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  // On login, merge the guest wishlist into the account and load the saved list
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const local = normWishlist(load("hb_wishlist", []));
        const { data } = local.length
          ? await api.post("/wishlist/merge", { items: local })
          : await api.get("/wishlist");
        setWishlist(normWishlist(data.items || []));
      } catch (err) { console.warn("Wishlist sync failed", err); }
    })();
  }, [userId]);

  const lineKey = (p, size, colour, personalisation) => `${p.id}::${size || ""}::${colour || ""}${personalisation ? `::${personalisation}` : ""}`;

  const addToCart = useCallback((product, { size = null, colour = null, quantity = 1, personalisation = null } = {}) => {
    const pv0 = !product.sale_price && (product.printify_variants || []).find((v) => (!size || v.size === size) && (!colour || v.colour === colour) && v.price);
    trackAddToCart({ id: product.id, name: product.name, quantity, price: pv0 ? pv0.price : (product.sale_price || product.price) });
    setCart((prev) => {
      const key = lineKey(product, size, colour, personalisation);
      const existing = prev.find((i) => i.key === key);
      if (existing) return prev.map((i) => i.key === key ? { ...i, quantity: i.quantity + quantity } : i);
      const pv = !product.sale_price && (product.printify_variants || []).find((v) => (!size || v.size === size) && (!colour || v.colour === colour) && v.price);
      const price = pv ? pv.price : (product.sale_price || product.price);
      return [...prev, {
        key, id: product.id, name: product.name, slug: product.slug, image: (product.images || [])[0],
        local_pickup: !!product.local_pickup, printify: !!product.printify,
        price, size, colour, quantity, personalisation,
      }];
    });
    setCartOpen(true);
  }, []);

  const removeFromCart = useCallback((key) => setCart((p) => p.filter((i) => i.key !== key)), []);

  const addBundle = useCallback(({ name, price, image, components, kind = "default" }) => {
    trackAddToCart({ type: "bundle", kind, name, price, quantity: 1 });
    setCart((prev) => [...prev, {
      key: `bundle-${Date.now()}`, type: "bundle", kind, name, price, image,
      components, quantity: 1,
    }]);
    setCartOpen(true);
  }, []);
  const updateQty = useCallback((key, qty) => setCart((p) =>
    p.map((i) => i.key === key ? { ...i, quantity: Math.max(1, qty) } : i)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((id, price = null, product = null) => {
    id = String(id);
    const has = wishlist.some((w) => w.id === id);
    if (user) (has ? api.delete(`/wishlist/${id}`) : api.post(`/wishlist/${id}`)).catch(() => {});
    if (!has && product) trackAddToWishlist(product);
    setWishlist((p) => (has ? p.filter((w) => w.id !== id) : [...p, { id, size: null, colour: null, saved_price: price }]));
  }, [user, wishlist]);
  const isWishlisted = useCallback((id) => wishlist.some((w) => w.id === String(id)), [wishlist]);

  // Move a cart line into the wishlist, keeping its chosen size/colour + price at save time.
  const saveForLater = useCallback((item) => {
    const id = String(item.id);
    const size = item.size ?? null, colour = item.colour ?? null, saved_price = item.price ?? null;
    setWishlist((p) => {
      if (p.some((w) => wKey(w.id, w.size, w.colour) === wKey(id, size, colour))) return p;
      if (user) api.post("/wishlist", { id, size, colour, saved_price }).catch(() => {});
      return [...p, { id, size, colour, saved_price, note: null }];
    });
    setCart((prev) => prev.filter((i) => i.key !== item.key));
  }, [user]);

  // Remove one specific saved entry (variant-aware), not all variants of the product.
  const removeWishlistEntry = useCallback((entry) => {
    const id = String(entry.id), size = entry.size ?? null, colour = entry.colour ?? null;
    setWishlist((p) => p.filter((w) => wKey(w.id, w.size, w.colour) !== wKey(id, size, colour)));
    if (user) api.post("/wishlist/remove", { id, size, colour }).catch(() => {});
  }, [user]);

  // Switch the saved size/colour of a wishlist entry in place (keeps saved_price + note).
  const updateWishlistVariant = useCallback((entry, newSize, newColour) => {
    const id = String(entry.id);
    newSize = newSize ?? null; newColour = newColour ?? null;
    const sp = entry.saved_price ?? null, note = entry.note ?? null;
    const oldK = wKey(id, entry.size, entry.colour);
    const newK = wKey(id, newSize, newColour);
    if (oldK === newK) return;
    setWishlist((p) => {
      if (p.some((w) => wKey(w.id, w.size, w.colour) === newK)) {
        return p.filter((w) => wKey(w.id, w.size, w.colour) !== oldK);
      }
      return p.map((w) => wKey(w.id, w.size, w.colour) === oldK ? { id, size: newSize, colour: newColour, saved_price: sp, note } : w);
    });
    if (user) {
      api.post("/wishlist/remove", { id, size: entry.size ?? null, colour: entry.colour ?? null })
        .then(() => api.post("/wishlist", { id, size: newSize, colour: newColour, saved_price: sp, note }))
        .catch(() => {});
    }
  }, [user]);

  // Add/edit a short gift note on a saved item (shows on the shared wishlist).
  const updateWishlistNote = useCallback((entry, note) => {
    const id = String(entry.id);
    note = (note || "").slice(0, 140);
    setWishlist((p) => p.map((w) =>
      wKey(w.id, w.size, w.colour) === wKey(id, entry.size, entry.colour) ? { ...w, note: note || null } : w));
    if (user) api.post("/wishlist/note", { id, size: entry.size ?? null, colour: entry.colour ?? null, note }).catch(() => {});
  }, [user]);

  // One-tap: put a saved item into the bag and remove it from the wishlist.
  const moveToCart = useCallback((entry) => {
    addToCart(entry.product, { size: entry.size ?? null, colour: entry.colour ?? null, quantity: 1 });
    removeWishlistEntry(entry);
  }, [addToCart, removeWishlistEntry]);

  // Move every in-stock, choice-complete saved item into the bag at once. Returns count moved.
  const moveManyToCart = useCallback((entries) => {
    const eligible = (entries || []).filter((e) => {
      const p = e.product;
      if (!p || (p.stock ?? 0) <= 0) return false;
      if ((p.sizes?.length || 0) > 0 && !e.size) return false;
      if ((p.colours?.length || 0) > 0 && !e.colour) return false;
      return true;
    });
    if (!eligible.length) return 0;
    eligible.forEach((e) => addToCart(e.product, { size: e.size ?? null, colour: e.colour ?? null, quantity: 1 }));
    const keys = new Set(eligible.map((e) => wKey(String(e.id), e.size, e.colour)));
    setWishlist((p) => p.filter((w) => !keys.has(wKey(w.id, w.size, w.colour))));
    if (user) eligible.forEach((e) =>
      api.post("/wishlist/remove", { id: String(e.id), size: e.size ?? null, colour: e.colour ?? null }).catch(() => {}));
    return eligible.length;
  }, [addToCart, user]);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <StoreContext.Provider value={{
      cart, wishlist, cartOpen, setCartOpen, addToCart, addBundle, removeFromCart, updateQty,
      clearCart, toggleWishlist, isWishlisted, saveForLater, removeWishlistEntry,
      updateWishlistVariant, updateWishlistNote, moveToCart, moveManyToCart, cartCount, subtotal,
    }}>
      {children}
    </StoreContext.Provider>
  );
}
